import React, { useEffect, useMemo, useRef, useState } from "react";
import "./styles.css";
import { HousingsActionCreator } from "../../store/housingsActions.ts";
import { RootState } from "../../store/index.ts";
import {
  // Жилая
  selectHousingsError,
  selectHousingsList,
  selectHousingsPending,
  // Коммерция
  selectCommerceHousingsList,
  selectCommerceHousingsPending,
  selectCommerceHousingsError,
} from "../../store/selectors.ts";
import { connect } from "react-redux";
import { HousingItem } from "./HousingItem";
import { HousingsSkeleton } from "./HousingsSkeleton";
import { Search } from "../../components/Search";
import { Error } from "../../components/common/Error";
import { PageNavigation } from "../../components/common/PageNavigation";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { ToTopButton } from "../../components/common/ToTopButton";

/** ▼ модалка, кнопка, иконка, фильтр */
import { Modal } from "../../components/common/Modal";
import { Button } from "../../components/common/Button";
import { ReactComponent as FiltersIcon } from "../../assets/icons/filters.svg";
import { DeadlineFilter } from "./DeadlineFilter";
import { parseDeadline } from "./DeadlineFilter/utils";
/** ▲ */

import { HHTabs, TabsMap } from "../../components/common/Tabs";
import {
  filterHousingsByClient,
} from "../../integrations/clientConfig";
import { useBranding } from "../../integrations/branding";

type DeadlineFilterState = {
  min: number | null;
  max: number | null;
  includeUnknown: boolean;
};

type HousingsViewState = {
  activeTab: string;
  deadlineFilter: DeadlineFilterState;
  selectedBuilders: string[];
  savedAt: number;
  scrollY: number;
  searchQuery: string;
  visibleCount: number;
};

type Props = {
  basePath?: string;
  enabledHousingTypes?: Array<"residential" | "commerce">;
  // Жилая
  pending: boolean;
  error: string | null;
  housings: any[] | null;
  // Коммерция
  commercePending: boolean;
  commerceError: string | null;
  commerceHousings: any[] | null;
  // Actions
  fetchFlatsTable: () => void;
  fetchHousings: () => void;
  fetchCommerceHousings: () => void;
};

type BuilderOption = {
  value: string;
  label: string;
};

const TABS: TabsMap = {
  residential: { id: "residential", name: "Жилая" },
  commerce: { id: "commerce", name: "Коммерческая" },
};

const INITIAL_VISIBLE_HOUSINGS = 12;
const VISIBLE_HOUSINGS_STEP = 12;
const HOUSINGS_VIEW_STATE_TTL = 30 * 60 * 1000;
const housingsViewStateCache = new Map<string, HousingsViewState>();

const createDefaultDeadlineFilter = (): DeadlineFilterState => ({
  min: null,
  max: null,
  includeUnknown: true,
});

const normalizeBuilderName = (value: unknown) =>
    String(value || "")
        .trim()
        .replace(/\s+/g, " ");

const normalizeBuilderKey = (value: unknown) =>
    normalizeBuilderName(value).toLocaleLowerCase("ru-RU");

const getCachedHousingsViewState = (key: string) => {
  const cachedState = housingsViewStateCache.get(key);

  if (!cachedState) return null;

  if (Date.now() - cachedState.savedAt > HOUSINGS_VIEW_STATE_TTL) {
    housingsViewStateCache.delete(key);
    return null;
  }

  return cachedState;
};

export const _Housings: React.FC<Props> = ({
                                             basePath = "",
                                             enabledHousingTypes = ["residential", "commerce"],
                                             // Жилая
                                             pending,
                                             error,
                                             housings,
                                             // Коммерция
                                             commercePending,
                                             commerceError,
                                             commerceHousings,
                                             // Actions
                                             fetchHousings,
                                             fetchCommerceHousings,
                                           }) => {
  const { config, isShowcase } = useBranding();
  const tabs = useMemo<TabsMap>(() => {
    const nextTabs: TabsMap = {};

    if (enabledHousingTypes.includes("residential")) {
      nextTabs.residential = TABS.residential;
    }

    if (enabledHousingTypes.includes("commerce")) {
      nextTabs.commerce = TABS.commerce;
    }

    return nextTabs;
  }, [enabledHousingTypes]);
  const defaultTabId = Object.values(tabs)[0]?.id ?? TABS.residential.id;
  const viewStateKey = basePath || "/";
  const initialViewStateRef = useRef<HousingsViewState | null>(
      getCachedHousingsViewState(viewStateKey),
  );
  const pendingScrollRestoreRef = useRef<HousingsViewState | null>(
      initialViewStateRef.current,
  );
  const initialActiveTab = initialViewStateRef.current?.activeTab;
  const [activeTab, setActiveTab] = useState<string>(
      initialActiveTab && tabs[initialActiveTab] ? initialActiveTab : defaultTabId,
  );

  const [searchResults, setSearchResults] = useState<any[] | undefined>();
  const [searchQuery, setSearchQuery] = useState(
      initialViewStateRef.current?.searchQuery || "",
  );
  const [deadlineFilter, setDeadlineFilter] = useState<DeadlineFilterState>(
      initialViewStateRef.current?.deadlineFilter || createDefaultDeadlineFilter(),
  );
  const [selectedBuilders, setSelectedBuilders] = useState<string[]>(
      initialViewStateRef.current?.selectedBuilders || [],
  );
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(
      initialViewStateRef.current?.visibleCount || INITIAL_VISIBLE_HOUSINGS,
  );
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Открыть карточку объекта
   * @param id ID объекта
   * @param type "residential" | "commerce"
   */
  const handleOpenHousing = (id: number, type: "residential" | "commerce") => {
    const routePrefix = basePath ? `${basePath}` : "";

    housingsViewStateCache.set(viewStateKey, {
      activeTab,
      deadlineFilter: { ...deadlineFilter },
      selectedBuilders: [...selectedBuilders],
      savedAt: Date.now(),
      scrollY: window.scrollY,
      searchQuery,
      visibleCount,
    });

    if (type === "commerce") {
      navigate(`${routePrefix}/commerce-complex/${id}/`, {
        state: { housingsReturnTo: `${location.pathname}${location.search}${location.hash}` },
      });
    } else {
      navigate(`${routePrefix}/housing-complex/${id}/`, {
        state: { housingsReturnTo: `${location.pathname}${location.search}${location.hash}` },
      });
    }
  };

  useEffect(() => {
    if (!tabs[activeTab]) {
      setActiveTab(defaultTabId);
    }
  }, [activeTab, defaultTabId, tabs]);

  // Фетч данных по активной вкладке
  useEffect(() => {
    if (activeTab === TABS.residential.id) {
      if (!housings) fetchHousings();
    } else {
      if (!commerceHousings) fetchCommerceHousings();
    }
    // Сохраняем состояние при первом рендере после возврата из карточки.
    if (!pendingScrollRestoreRef.current) {
      setSearchResults(undefined);
      setSearchQuery("");
      setDeadlineFilter(createDefaultDeadlineFilter());
      setSelectedBuilders([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Прокрутка вверх при загрузке данных
  useEffect(() => {
    if (pendingScrollRestoreRef.current) return;
    if (activeTab === TABS.residential.id && housings) window.scrollTo(0, 0);
    if (activeTab === TABS.commerce.id && commerceHousings) window.scrollTo(0, 0);
  }, [activeTab, housings, commerceHousings]);

  // Текущие данные зависят от таба
  const currentPending =
      activeTab === TABS.residential.id ? pending : commercePending;
  const currentError =
      activeTab === TABS.residential.id ? error : commerceError;
  const sourceHousings =
      activeTab === TABS.residential.id ? housings : commerceHousings;
  const currentHousings = useMemo(
      () =>
          filterHousingsByClient(
              sourceHousings,
              activeTab === TABS.residential.id ? "residential" : "commerce",
              config,
          ),
      [activeTab, config, sourceHousings],
  );

  const builderOptions = useMemo<BuilderOption[]>(() => {
    const builders = new Map<string, string>();

    (currentHousings || []).forEach((housing: any) => {
      const builder = normalizeBuilderName(housing?.builder);
      if (!builder) return;

      const normalizedKey = normalizeBuilderKey(builder);
      if (!builders.has(normalizedKey)) builders.set(normalizedKey, builder);
    });

    return Array.from(builders.values())
        .sort((left, right) => left.localeCompare(right, "ru-RU"))
        .map((builder) => ({ value: builder, label: builder }));
  }, [currentHousings]);

  const selectedBuilderOptions = useMemo(
      () => builderOptions.filter((option) => selectedBuilders.includes(option.value)),
      [builderOptions, selectedBuilders],
  );
  const selectedBuilderKeys = useMemo(
      () => new Set(selectedBuilders.map(normalizeBuilderKey)),
      [selectedBuilders],
  );

  const filtered = useMemo(() => {
    const housingArray = searchResults || currentHousings || [];
    if (!housingArray?.length) return [];
    const { min, max, includeUnknown } = deadlineFilter;
    return housingArray.filter((h: any) => {
      if (selectedBuilders.length) {
        if (!selectedBuilderKeys.has(normalizeBuilderKey(h?.builder))) return false;
      }

      if (min == null || max == null) return true;
      const { sortKey } = parseDeadline(h.deadline ?? "");
      if (sortKey == null) return includeUnknown;
      return sortKey >= min && sortKey <= max;
    });
  }, [searchResults, currentHousings, deadlineFilter, selectedBuilderKeys, selectedBuilders.length]);

  const hasActiveDeadlineFilter =
      deadlineFilter.min !== null || deadlineFilter.max !== null || !deadlineFilter.includeUnknown;
  const hasActiveBuilderFilter = selectedBuilders.length > 0;
  const activeFiltersCount = Number(hasActiveDeadlineFilter) + Number(hasActiveBuilderFilter);
  const totalCount = currentHousings?.length || 0;
  const resultCount = filtered.length;
  const visibleHousings = useMemo(
      () => filtered.slice(0, visibleCount),
      [filtered, visibleCount],
  );
  const hasMoreHousings = visibleHousings.length < filtered.length;
  const pageContent =
      activeTab === TABS.commerce.id
          ? config.housingsPage.tabs.commerce
          : config.housingsPage.tabs.residential;

  const handleFiltersApply = () => setIsFiltersOpen(false);
  const handleFiltersClear = () => {
    setDeadlineFilter(createDefaultDeadlineFilter());
    setSelectedBuilders([]);
  };

  useEffect(() => {
    if (pendingScrollRestoreRef.current) return;
    setVisibleCount(INITIAL_VISIBLE_HOUSINGS);
  }, [activeTab, searchResults, deadlineFilter, selectedBuilders]);

  useEffect(() => {
    const savedViewState = pendingScrollRestoreRef.current;

    if (!savedViewState || currentPending || !sourceHousings) return undefined;
    if (savedViewState.searchQuery && searchResults === undefined) return undefined;

    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        window.scrollTo({ top: savedViewState.scrollY, left: 0, behavior: "auto" });
        housingsViewStateCache.delete(viewStateKey);
        pendingScrollRestoreRef.current = null;
        initialViewStateRef.current = null;
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame) window.cancelAnimationFrame(secondFrame);
    };
  }, [currentPending, searchResults, sourceHousings, viewStateKey, visibleHousings.length]);

  useEffect(() => {
    if (!hasMoreHousings) {
      return undefined;
    }

    const target = loadMoreRef.current;
    if (!target || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0]?.isIntersecting) {
            return;
          }

          setVisibleCount((current) =>
              Math.min(current + VISIBLE_HOUSINGS_STEP, filtered.length),
          );
        },
        {
          rootMargin: "400px 0px",
        },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [filtered.length, hasMoreHousings]);

  if (currentPending) return <HousingsSkeleton />;
  if (currentError) {
    const retry =
        activeTab === TABS.residential.id ? fetchHousings : fetchCommerceHousings;
    return (
        <div className="housings_container">
          <PageNavigation />
          <Error retryCallback={retry} />
        </div>
    );
  }

  return (
      <>
        <div className="housings_container">
          <PageNavigation />

          <div className="housings_header">
            <div className="housings_heading">
              <h1 className="housings_title">{pageContent.title}</h1>
              <div className="housings_count">{resultCount}</div>
            </div>
            <p className="housings_description">{pageContent.description}</p>
          </div>

          <div className="housings_toolbar">
            <div className="housings_search-slot">
              <Search
                  onResult={setSearchResults}
                  housings={currentHousings}
                  placeholder={config.searchPlaceholder}
                  value={searchQuery}
                  onChange={setSearchQuery}
              />
            </div>

            {Object.keys(tabs).length > 1 ? (
              <div className="housings_tabs-wrap">
                <HHTabs
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    tabs={tabs}
                    className="mt-0"
                    listClassName="housings_tabs-list"
                    triggerClassName="housings_tabs-trigger"
                    equalWidth
                />
              </div>
            ) : null}

            <button
              type="button"
              aria-label="Открыть фильтры"
              onClick={() => setIsFiltersOpen(true)}
              className={`housings_filter-button ${activeFiltersCount ? "is-active" : ""}`}
            >
              <FiltersIcon width="18" height="18" />
              <span>{config.housingsPage.filtersButtonLabel}</span>
              {activeFiltersCount ? (
                <span className="housings_filter-badge">{activeFiltersCount}</span>
              ) : null}
            </button>
          </div>

          <div className="housings_meta">
            <div className="housings_meta-group">
              <span className="housings_meta-label">
                {config.housingsPage.totalObjectsLabel}
              </span>
              <span className="housings_meta-value">{totalCount}</span>
            </div>
            <div className="housings_meta-group">
              <span className="housings_meta-label">
                {config.housingsPage.shownObjectsLabel}
              </span>
              <span className="housings_meta-value">{resultCount}</span>
            </div>
            {hasActiveDeadlineFilter ? (
                <button
                    type="button"
                    className="housings_reset-button"
                    onClick={handleFiltersClear}
                >
                  {config.housingsPage.resetFiltersLabel}
                </button>
            ) : null}
          </div>

          <div className="housings_filter-summary">
            <span className="housings_summary-pill">
              {resultCount === totalCount
                  ? config.housingsPage.allObjectsLabel
                  : config.housingsPage.foundObjectsLabel(
                      resultCount,
                      totalCount,
                  )}
            </span>
            {deadlineFilter.min !== null && deadlineFilter.max !== null ? (
                <span className="housings_summary-pill">
                  {config.housingsPage.deadlineRangeLabel(
                      Math.floor((deadlineFilter.min - 1) / 12),
                      Math.floor((deadlineFilter.max - 1) / 12),
                  )}
                </span>
            ) : null}
            {!deadlineFilter.includeUnknown ? (
                <span className="housings_summary-pill">
                  {config.housingsPage.excludeUnknownDeadlinesLabel}
                </span>
            ) : null}
            {hasActiveBuilderFilter ? (
                <span className="housings_summary-pill">
                  {selectedBuilders.length === 1
                      ? `Застройщик: ${selectedBuilders[0]}`
                      : `Застройщики: ${selectedBuilders.length}`}
                </span>
            ) : null}
          </div>

          {filtered.length === 0 ? (
              <section className="housings_empty-state">
                <div className="housings_empty-title">{pageContent.emptyTitle}</div>
                <div className="housings_empty-text">
                  {pageContent.emptyDescription}
                </div>
                <Button
                    text={config.housingsPage.resetFiltersLabel}
                    onClick={handleFiltersClear}
                    variant="secondary"
                />
              </section>
          ) : (
                  <>
                    <ul className="housings_list">
                      {visibleHousings.map((housing: any) => (
                          <HousingItem
                              key={housing.id}
                              housing={housing}
                              labels={config.housingCard}
                              onClick={() =>
                                  handleOpenHousing(
                                      housing.id,
                                      activeTab === TABS.commerce.id ? "commerce" : "residential",
                                  )
                              }
                          />
                      ))}
                    </ul>
                    {hasMoreHousings ? (
                        <>
                          <div ref={loadMoreRef} className="housings_load-trigger" aria-hidden="true" />
                          <div className="housings_load-status">Загружаем ещё объекты...</div>
                        </>
                    ) : null}
                  </>
          )}

          {/* Модальное окно с фильтром срока сдачи (по годам) */}
          <Modal
              title="Фильтры"
              isOpen={isFiltersOpen}
              onClose={() => setIsFiltersOpen(false)}
              actionButtonText="применить"
              actionButtonCallback={handleFiltersApply}
              actionButtonClassName={isShowcase ? "housings_filters-apply-button" : ""}
              className={`housings_filters-modal ${isShowcase ? "housings_filters-modal-showcase" : ""}`.trim()}
          >
            <div className="my-4">
              <div className="housings_filters-modal-body">
                <div className="housings_builder-filter">
                  <label className="housings_builder-filter-label" htmlFor="housings-builder-filter">
                    Застройщики
                  </label>
                  <Select<BuilderOption, true>
                      inputId="housings-builder-filter"
                      options={builderOptions}
                      value={selectedBuilderOptions}
                      onChange={(options) => setSelectedBuilders(options.map((option) => option.value))}
                      isMulti
                      isSearchable
                      closeMenuOnSelect={false}
                      classNamePrefix="rf"
                      placeholder="Все застройщики"
                      noOptionsMessage={() => "Застройщики не найдены"}
                      menuPlacement="auto"
                  />
                </div>
                <DeadlineFilter
                    housings={currentHousings || []}
                    value={deadlineFilter}
                    onChange={setDeadlineFilter}
                />
              </div>
            </div>

            <Button
                className={`mb-4 ${isShowcase ? "housings_filters-reset-button" : ""}`.trim()}
                text="сброс"
                onClick={handleFiltersClear}
                fullWidth
                variant="secondary"
            />
          </Modal>
        </div>
        <ToTopButton />
      </>
  );
};

const mapDispatchToProps = {
  fetchFlatsTable: HousingsActionCreator.fetchFlatsTable,
  fetchHousings: HousingsActionCreator.fetchHousings,
  fetchCommerceHousings: HousingsActionCreator.fetchCommerceHousings,
};

const mapStateToProps = (state: RootState) => ({
  // Жилая
  pending: selectHousingsPending(state),
  error: selectHousingsError(state),
  housings: selectHousingsList(state),
  // Коммерция
  commercePending: selectCommerceHousingsPending(state),
  commerceError: selectCommerceHousingsError(state),
  commerceHousings: selectCommerceHousingsList(state),
});

export const Housings = connect(mapStateToProps, mapDispatchToProps)(_Housings);
